// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R

import styles from './index.module.scss';
import { mergeClasses, normalizeNumber } from 'util/commons';
import { InfoBlock } from 'components/molecules/MyPage/InfoBlock';
import { Field } from 'components/molecules/MyPage/Field';
import { EmailListField } from 'components/molecules/MyPage/EmailListField';
import { useTranslation } from 'react-i18next';
import { CollectAdminSiteMyPageFormData, MyPageType } from 'types/mypage';
import { useSelector } from 'react-redux';
import { selectUser } from 'redux/slices/userSlice';
import { useMypageContactInfo } from 'util/hooks/useMypageContactInfo';
import Toggle from 'components/atoms/Toggle';
import { useRef, useState } from 'react';

export const CollectionAdminSite = () => {
  const { t } = useTranslation();
  const user = useSelector(selectUser);
  const hasValidUser = !!user.userId && !!user.role;

  const myPage = useMypageContactInfo<MyPageType.CollectionAdminSite>({
    myPageType: MyPageType.CollectionAdminSite,
    enabled: hasValidUser,
  });
  const data = myPage.data;

  const handleChange = (
    formData: CollectAdminSiteMyPageFormData
  ): Promise<void> => {
    return myPage.handleChangeInfo(
      formData as Parameters<typeof myPage.handleChangeInfo>[0]
    );
  };

  const [pendingToggleOn, setPendingToggleOn] = useState(false);
  const isClickingToggleRef = useRef(false);

  const handleToggle = (value: boolean) => {
    isClickingToggleRef.current = false;
    const emailsEmpty = !data?.emails?.length;
    if (pendingToggleOn && !value) {
      setPendingToggleOn(false);
      return;
    }
    if (value && emailsEmpty) {
      setPendingToggleOn(true);
    } else {
      setPendingToggleOn(false);
      handleChange({ enableEmailNotification: value });
    }
  };

  return (
    <div className={styles.mypage}>
      <div className={styles.generalInfo}>
        <InfoBlock
          className={styles.accountInfo}
          title={t('my_page.account_info.title')}
        >
          <div className={styles.col}>
            <Field
              label={t('my_page.account_info.account_category')}
              value={t('my_page.account_info.collection')}
            />
            <Field
              label={t('my_page.account_info.account_permissions')}
              value={t('my_page.account_info.admin_site') || '拠点管理者'}
            />
          </div>
        </InfoBlock>

        <InfoBlock
          className={styles.masterInfo}
          title={
            t('my_page.collection_admin_site.master_info.title') ||
            '拠点マスタ情報'
          }
        >
          <div className={styles.grid}>
            <Field
              label={
                t('my_page.collection_admin_site.master_info.company_name') ||
                '事業者名'
              }
              value={data?.siteName}
            />

            <Field
              label={
                t(
                  'my_page.collection_admin_site.master_info.driver_count_label'
                ) || '管理ドライバー'
              }
              value={`${t(
                'my_page.collection_admin_operator.master_info.driver_count',
                {
                  driverCount: normalizeNumber({
                    value: data?.driverCount || 0,
                  }),
                  manyDriver: data?.driverCount === 1 ? '' : 's',
                }
              )}`}
            />
            <div />

            <div className={styles.fieldToggle}>
              <p className={styles.fieldToggleLabel}>
                {t(
                  'my_page.collection_admin_site.master_info.email_notification'
                ) || 'メール通知受信'}
              </p>
              <div
                onMouseDown={() => {
                  isClickingToggleRef.current = true;
                }}
              >
                <Toggle
                  value={data?.enableEmailNotification || pendingToggleOn}
                  setValue={handleToggle}
                  isDarkMode
                />
              </div>
            </div>

            <EmailListField
              label={
                t('my_page.collection_admin_site.master_info.mail_address') ||
                '通知メールアドレス'
              }
              values={data?.emails ?? []}
              placeholder={
                t('my_page.collection_admin_site.master_info.mail_address') ||
                ''
              }
              maxCount={3}
              defaultEditing={pendingToggleOn}
              onCancel={() => {
                if (!isClickingToggleRef.current) {
                  setPendingToggleOn(false);
                }
              }}
              required={pendingToggleOn || data?.enableEmailNotification}
              onEdit={(emails) => {
                if (pendingToggleOn) {
                  setPendingToggleOn(false);
                  return handleChange({
                    enableEmailNotification: true,
                    emails: emails,
                  });
                }
                return handleChange({ emails: emails });
              }}
            />
          </div>
        </InfoBlock>
      </div>

      <div className={styles.additionalInfo}>
        <InfoBlock
          title={
            t('my_page.collection_admin_operator.material_list.title') ||
            '管理材料'
          }
        >
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>
                    {t(
                      'my_page.collection_admin_operator.material_list.material_name'
                    )}
                  </th>
                  <th>
                    {t(
                      'my_page.collection_admin_operator.material_list.delivery_site_name'
                    )}
                  </th>
                </tr>
              </thead>

              <tbody>
                {data?.materials.length ? (
                  data.materials.map((material, index) => (
                    <tr key={index}>
                      <td className={mergeClasses(styles.bold, styles.light)}>
                        <p title={material.name}>{material.name}</p>
                      </td>
                      <td>
                        <p title={material.deliverySiteName}>
                          {material.deliverySiteName}
                        </p>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={2} className={styles.noData}>
                      {t('messages.M_008')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </InfoBlock>
      </div>
    </div>
  );
};
